import React, { useEffect, useState } from "react";
import "./addClient.css";
import api from "../../../api";

// ─── helpers ────────────────────────────────────────────────────────────────

const EMPTY_ROW = () => ({
  _id:            Math.random().toString(36).slice(2),
  partyEstRateId: "",
  productId:      "",
  qty:            "",
  rate:           "",
});

const buildEstString = (rows) =>
  rows
    .filter((r) => r.productId || r.qty || r.rate)
    .map((r) => `${r.partyEstRateId}!${r.productId}!${r.qty}!${r.rate}!$`)
    .join("");

const TOP_FIELDS = [
  { key: "name",                  label: "Client Name"         },
  { key: "phone",                 label: "Mobile"              },
  { key: "contactPerson",         label: "Contact Person"      },
  { key: "email",                 label: "Email"               },
  { key: "paymentTerms",          label: "Payment Terms"       },
  { key: "currentSupplier",       label: "Current Supplier"    },
  { key: "buyingFrequency",       label: "Buying Frequency"    },
  { key: "estMonthlyConsumption", label: "Monthly Consumption" },
];

// ─── component ──────────────────────────────────────────────────────────────

export const AddClient = ({ onClose, mode = "add", partyId = null }) => {
  const salesPersonId = localStorage.getItem("salesPersonId");
  const [productList,    setProductList]    = useState([]);
  const [form,           setForm]           = useState({
    name: "", contactPerson: "", phone: "", email: "",
    paymentTerms: "", currentSupplier: "", buyingFrequency: "",
    estMonthlyConsumption: "",
  });
  const [rows,           setRows]           = useState([EMPTY_ROW()]);
  const [savedPartyId,   setSavedPartyId]   = useState(null);
  const [savedOnce,      setSavedOnce]      = useState(false);
  const [isLocked,       setIsLocked]       = useState(false);
  const [checkedIds,     setCheckedIds]     = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [sendingQuote,   setSendingQuote]   = useState(false);
  const [saveMsg,        setSaveMsg]        = useState("");
  const [quoteMsg,       setQuoteMsg]       = useState("");
  const [errors,         setErrors]         = useState({});

  // ── fetch product list ──
  useEffect(() => {
    api.get("/api/productlist")
      .then((r) => setProductList(r.data?.data || []))
      .catch((e) => console.error("Product list error:", e));
  }, []);

  // ── update mode: pre-populate ──
  useEffect(() => {
    if (mode !== "update" || !partyId) return;
    api.get("/api/partydetail", { params: { PartyId: partyId } })
      .then((r) => {
        const d = r.data?.data;
        if (!d) return;
        const p = d.party?.[0] || {};
        setForm({
          name:                   p.PartyName                  || "",
          contactPerson:          p.ConPersonName              || "",
          phone:                  p.ConMobile                  || "",
          email:                  p.ConEmail                   || "",
          paymentTerms:           p.ParPaymentTerms            || "",
          currentSupplier:        p.ParCurrentSupplier         || "",
          buyingFrequency:        p.ParEstBuyingFrequncy       || "",
          estMonthlyConsumption:  p.ParEstMonthlyConsumption   || "",
        });
        const estRows = (d.estRates || []).map((er) => ({
          _id:            Math.random().toString(36).slice(2),
          partyEstRateId: er.PartyEstRateId     ?? "",
          productId:      er.ParEstProductId    ?? "",
          qty:            er.ParEstMonthlyRqQty ?? "",
          rate:           er.ParEstRate         ?? "",
        }));
        if (estRows.length > 0) setRows(estRows);
        setSavedPartyId(partyId);
        setSavedOnce(true);
        setIsLocked(true);
      })
      .catch((e) => console.error("Party detail error:", e));
  }, [mode, partyId]);

  // ─── field helpers ──────────────────────────────────────────────────────
  const setFormField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: false }));
  };

  const setRowField = (id, key, val) => {
    setRows((prev) => prev.map((r) => r._id === id ? { ...r, [key]: val } : r));
  };

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r._id !== id));

  // ─── validation ─────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.name?.toString().trim()) errs.name = true;
    const needsContact = !!form.phone?.toString().trim() || !!form.email?.toString().trim();
    if (needsContact && !form.contactPerson?.toString().trim()) errs.contactPerson = true;

    // Only validate rows that have at least one field filled (partial rows are invalid)
    rows.forEach((r) => {
      const hasAny = r.productId || r.qty || r.rate;
      if (hasAny) {
        if (!r.productId) errs[`prod_${r._id}`] = true;
        if (!r.qty)       errs[`qty_${r._id}`]  = true;
        if (!r.rate)      errs[`rate_${r._id}`] = true;
      }
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── parse response → { newForm, newRows, newPartyId } ──────────────────
  const parseResponse = (data) => {
    const p = data.party?.[0] || {};
    const newForm = {
      name:                   p.PartyName                  || "",
      contactPerson:          p.ConPersonName              || "",
      phone:                  p.ConMobile                  || "",
      email:                  p.ConEmail                   || "",
      paymentTerms:           p.ParPaymentTerms            || "",
      currentSupplier:        p.ParCurrentSupplier         || "",
      buyingFrequency:        p.ParEstBuyingFrequncy       || "",
      estMonthlyConsumption:  p.ParEstMonthlyConsumption   || "",
    };
    const newRows = (data.estRates || []).map((er) => ({
      _id:            Math.random().toString(36).slice(2),
      partyEstRateId: er.PartyEstRateId     ?? "",
      productId:      er.ParEstProductId    ?? "",
      qty:            er.ParEstMonthlyRqQty ?? "",
      rate:           er.ParEstRate         ?? "",
    }));
    const newPartyId = p.PartyId || null;
    return { newForm, newRows, newPartyId };
  };

  // ─── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) {
      setSaveMsg("Please fill all required fields.");
      setTimeout(() => setSaveMsg(""), 3000);
      return;
    }
    setSaving(true);
    setSaveMsg("");
    try {
      const isUpdate = mode === "update" || !!savedPartyId;
      const payload = {
        SaveType:               isUpdate ? "U" : "I",
        ...(isUpdate && { PartyId: savedPartyId ?? partyId }),
        Name:                   form.name,
        ContactPerson:          form.contactPerson,
        Phone:                  form.phone,
        Email:                  form.email,
        SalesPersonId:          salesPersonId,
        PaymentTerms:           form.paymentTerms,
        CurrentSupplier:        form.currentSupplier,
        BuyingFrequency:        form.buyingFrequency,
        EstMonthlyConsumption:  form.estMonthlyConsumption,
        UserCode:               "ADMIN",
        EstProductRateString:   buildEstString(rows),
      };

      const res  = await api.post("/api/partyestratedtladdupdate", payload);
      const data = res.data?.data;

      if (data) {
        const { newForm, newRows, newPartyId } = parseResponse(data);
        setForm(newForm);
        setRows(newRows.length > 0 ? newRows : [EMPTY_ROW()]);
        if (newPartyId) setSavedPartyId(newPartyId);
      }

      setSavedOnce(true);
      setIsLocked(true);
      setCheckedIds([]);
      setSaveMsg("Saved successfully!");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      console.error("Save error:", e);
      setSaveMsg("Save failed. Please try again.");
      setTimeout(() => setSaveMsg(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ─── edit ────────────────────────────────────────────────────────────────
  const handleEdit = () => {
    setIsLocked(false);
    setCheckedIds([]);
  };

  // ─── send quote ──────────────────────────────────────────────────────────
  const canSendQuote = savedOnce && isLocked && checkedIds.length > 0;

  const handleSendQuote = async () => {
    if (!canSendQuote) return;
    setSendingQuote(true);
    setQuoteMsg("");
    try {
      await api.post("/api/emailquote", { partyestimaterateid: checkedIds.join(",") });
      setQuoteMsg("Quote sent successfully!");
      setTimeout(() => setQuoteMsg(""), 3000);
    } catch (e) {
      console.error("Send quote error:", e);
      setQuoteMsg("Failed to send quote.");
      setTimeout(() => setQuoteMsg(""), 3000);
    } finally {
      setSendingQuote(false);
    }
  };

  // ─── checkbox toggle ────────────────────────────────────────────────────
  const toggleCheck = (partyEstRateId) => {
    const id = String(partyEstRateId);
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ─── render ─────────────────────────────────────────────────────────────
  return (
    <div className="addClientModalOverlay">
      <div className="addClientModalMain">

        {/* Close */}
        <div className="addClientCloseWrapper">
          <span className="material-symbols-outlined addClientCloseIcon" onClick={onClose}>
            close
          </span>
        </div>

        {/* ── Info fields ── */}
        <div className="addClientInfoWrapper">
          <table className="addClientInfoTable">
            <tbody>
              <tr>
                <td className={`addClientLabelCell${errors.name ? " addClientLabelError" : ""}`}>
                  Client Name *
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.name}
                    disabled={isLocked}
                    onChange={(e) => setFormField("name", e.target.value)}
                  />
                </td>
                <td className={`addClientLabelCell${errors.contactPerson ? " addClientLabelError" : ""}`}>
                  Contact Person{(form.phone?.trim() || form.email?.trim()) ? " *" : ""}
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.contactPerson}
                    disabled={isLocked}
                    onChange={(e) => setFormField("contactPerson", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td className={`addClientLabelCell${errors.phone ? " addClientLabelError" : ""}`}>
                  Mobile
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.phone}
                    disabled={isLocked}
                    onChange={(e) => setFormField("phone", e.target.value)}
                  />
                </td>
                <td className={`addClientLabelCell${errors.email ? " addClientLabelError" : ""}`}>
                  Email
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.email}
                    disabled={isLocked}
                    onChange={(e) => setFormField("email", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td className={`addClientLabelCell${errors.paymentTerms ? " addClientLabelError" : ""}`}>
                  Payment Terms
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.paymentTerms}
                    disabled={isLocked}
                    onChange={(e) => setFormField("paymentTerms", e.target.value)}
                  />
                </td>
                <td className={`addClientLabelCell${errors.currentSupplier ? " addClientLabelError" : ""}`}>
                  Current Supplier
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.currentSupplier}
                    disabled={isLocked}
                    onChange={(e) => setFormField("currentSupplier", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td className={`addClientLabelCell${errors.buyingFrequency ? " addClientLabelError" : ""}`}>
                  Buying Frequency
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.buyingFrequency}
                    disabled={isLocked}
                    onChange={(e) => setFormField("buyingFrequency", e.target.value)}
                  />
                </td>
                <td className={`addClientLabelCell${errors.estMonthlyConsumption ? " addClientLabelError" : ""}`}>
                  Monthly Consumption (₹)
                </td>
                <td>
                  <input
                    className="addClientInput"
                    value={form.estMonthlyConsumption}
                    disabled={isLocked}
                    onChange={(e) => setFormField("estMonthlyConsumption", e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Product mix header ── */}
        <div className="addClientProductHeaderWrapper">
          <p className="addClientProductHeaderText">Current product mix</p>
          {!isLocked && (
            <button className="addClientAddRowBtn" onClick={addRow}>
              <span className="material-symbols-outlined">add</span>
              Add Row
            </button>
          )}
        </div>

        {/* ── Product table ── */}
        <div className="addClientProductTableWrapper">
          <table className="addClientProductTable">
            <thead>
              <tr>
                <th style={{ width: "4%" }}></th>
                <th style={{ width: "38%" }}>PRODUCT</th>
                <th style={{ width: "22%" }}>EST. MONTHLY QTY</th>
                <th style={{ width: "22%" }}>RATE PER UNIT</th>
                <th style={{ width: "8%" }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const canCheck  = isLocked && !!row.partyEstRateId;
                const isChecked = checkedIds.includes(String(row.partyEstRateId));
                return (
                  <tr key={row._id}>
                    <td>
                      <input
                        type="checkbox"
                        className="addClientCheckbox"
                        checked={isChecked}
                        disabled={!canCheck}
                        onChange={() => toggleCheck(row.partyEstRateId)}
                      />
                    </td>
                    <td className="addClientDropdownCell">
                      <div className={`addClientSelectWrapper${errors[`prod_${row._id}`] ? " addClientCellError" : ""}`}>
                        <select
                          className="addClientProductSelect"
                          value={row.productId}
                          disabled={isLocked}
                          onChange={(e) => setRowField(row._id, "productId", e.target.value)}
                        >
                          <option value="">Select product...</option>
                          {productList.map((p) => (
                            <option key={p.ProductId} value={p.ProductId}>
                              {p.ProductName}
                            </option>
                          ))}
                        </select>
                        <span className="material-symbols-outlined addClientSelectArrow">expand_more</span>
                      </div>
                    </td>
                    <td>
                      <input
                        className={`addClientTableInput${errors[`qty_${row._id}`] ? " addClientInputError" : ""}`}
                        type="number"
                        min="0"
                        placeholder="Qty"
                        value={row.qty}
                        disabled={isLocked}
                        onChange={(e) => setRowField(row._id, "qty", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className={`addClientTableInput${errors[`rate_${row._id}`] ? " addClientInputError" : ""}`}
                        type="number"
                        min="0"
                        placeholder="Rate"
                        value={row.rate}
                        disabled={isLocked}
                        onChange={(e) => setRowField(row._id, "rate", e.target.value)}
                      />
                    </td>
                    <td>
                      {!isLocked && rows.length > 1 && (
                        <span
                          className="material-symbols-outlined addClientRemoveRow"
                          onClick={() => removeRow(row._id)}
                        >
                          remove_circle_outline
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Footer ── */}
        <div className="addClientFooterWrapper">
          {/* Send Quote */}
          <div className="addClientQuoteWrapper">
            <p
              className={`addClientPrepareQuoteText${canSendQuote ? "" : " addClientPrepareQuoteDisabled"}`}
              onClick={handleSendQuote}
              title={
                !savedOnce              ? "Save first before sending quote" :
                !isLocked               ? "Save changes before sending quote" :
                checkedIds.length === 0 ? "Select at least one product row" :
                "Send Quote"
              }
            >
              {sendingQuote ? "Sending..." : "Send Quote"}
            </p>
            {!savedOnce && (
              <p className="addClientQuoteHint">Save first to enable Send Quote</p>
            )}
            {savedOnce && !isLocked && (
              <p className="addClientQuoteHint">Save changes to enable quote</p>
            )}
            {savedOnce && isLocked && checkedIds.length === 0 && (
              <p className="addClientQuoteHint">Select rows to send quote</p>
            )}
          </div>

          {/* Messages */}
          <div className="addClientMsgArea">
            {saveMsg && (
              <p className={`addClientMsg ${saveMsg.includes("success") ? "addClientMsgSuccess" : "addClientMsgError"}`}>
                {saveMsg}
              </p>
            )}
            {quoteMsg && (
              <p className={`addClientMsg ${quoteMsg.includes("success") ? "addClientMsgSuccess" : "addClientMsgError"}`}>
                {quoteMsg}
              </p>
            )}
          </div>

          {/* Edit / Save */}
          <div className="addClientActionBtns">
            {savedOnce && isLocked ? (
              <p className="addClientEditText" onClick={handleEdit}>Edit</p>
            ) : (
              <p
                className={`addClientSaveText${saving ? " addClientSaveDisabled" : ""}`}
                onClick={!saving ? handleSave : undefined}
              >
                {saving ? "Saving..." : "Save"}
              </p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};